import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowRight, TrendingUp, ShieldCheck, FileCheck, HeartPulse, Hammer, Sparkles, Lock, Star, Layers, Activity, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { OrganicShapes } from '../components/decor/OrganicShapes';
import { Reveal } from '../components/Reveal';

const outcomes = [
  {
    icon: TrendingUp,
    title: 'Move Stars with intervention that happens in time',
    modules: 'StarIQ',
  },
  {
    icon: ShieldCheck,
    title: 'Protect shared savings across ACO + payer contracts',
    modules: 'AccountableIQ, Attribution Hub',
  },
  {
    icon: FileCheck,
    title: 'Defensible risk adjustment that survives RADV',
    modules: 'Risk Adjustment Tools',
  },
  {
    icon: HeartPulse,
    title: 'Reduce MLR with clinical context beside the financial signal',
    modules: 'HEDISIQ, Care Management',
  },
];

const solutions = [
  {
    icon: Hammer,
    title: 'Forward Deployed Engineering (FDE)',
    description: 'We embed across your operations; quality, risk, and finance, claims, prior authorization, payment integrity, and member experience building custom data and AI solutions in your own tools, accountable from first build through production.',
    bullets: ['AI engineering for any payer workflow, not a standard template', 'Accountable through production, not just at launch'],
    color: 'text-[#34AFFB]',
    bgColor: 'bg-[#34AFFB]/10',
  },
  {
    icon: Sparkles,
    title: 'Agentic AI',
    description: 'We build AI agents that take the work off your team entirely, automating gap closure, risk workflows, prior authorization, claims review, and member outreach autonomously, with human oversight where the stakes demand it.',
    bullets: ['Agents that run gap closure, prior auth & claims at scale', 'Autonomy where it helps, human-in-the-loop where it matters'],
    color: 'text-[#70D6C5]',
    bgColor: 'bg-[#70D6C5]/10',
  },
  {
    icon: Star,
    title: 'Stars & Quality Intelligence',
    description: 'We forecast Star cut points and simulate your contract rating, surfacing HEDIS, CAHPS, and pharmacy-adherence risk early so quality teams intervene while the measurement year is still open, not after ratings publish.',
    bullets: ['Cut points & contract rating forecast before CMS publishes', 'HEDIS, CAHPS & adherence risk surfaced in time to act'],
    color: 'text-[#FF7F66]',
    bgColor: 'bg-[#FF7F66]/10',
  },
  {
    icon: FileCheck,
    title: 'Risk-adjustment Intelligence',
    description: 'We surface missed and suspected HCC conditions across your membership and flag unsupported codes for removal so every diagnosis rests on defensible evidence that holds up against RADV.',
    bullets: ['Prospective & retrospective capture across the population', 'Codes added and removed on RADV-defensible evidence'],
    color: 'text-[#34AFFB]',
    bgColor: 'bg-[#34AFFB]/10',
  },
  {
    icon: Layers,
    title: 'Ontologies & Semantic Data Models',
    description: 'We map fragmented claims, enrollment, pharmacy, and clinical feeds to a shared ontology, one FHIR-native model where members, providers, and conditions are defined once, so every downstream model and agent reasons over the same trusted truth.',
    bullets: ['A unifying ontology across claims, pharmacy, clinical & SDOH', 'Normalized vocabularies, matched members & providers, one shared meaning'],
    color: 'text-[#70D6C5]',
    bgColor: 'bg-[#70D6C5]/10',
  },
  {
    icon: Activity,
    title: 'Predictive & MLR Analytics',
    description: 'Predictive models that pair clinical context with the financial signal to find rising-risk members before they surface in claims, flagging avoidable admissions, ED visits, and specialty-drug spend early, so utilization and medical loss ratio bend before the quarter closes.',
    bullets: ['Rising-risk members caught before they become high-cost', 'Avoidable utilization & specialty spend flagged for action'],
    color: 'text-[#FF7F66]',
    bgColor: 'bg-[#FF7F66]/10',
  },
];

export function PayerPage() {
  return (
    <div className="min-h-screen">
      <Header />

      {/* 1. Their reality */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f9fbfc] via-white to-[#F4F7F9] py-12 lg:py-16">
        <OrganicShapes variant="hero" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal as="div" direction="right">
              <span className="border-l-4 border-[#34AFFB] pl-3 text-[#34AFFB] text-sm font-semibold inline-block mb-6">
                Payers & Health Plans
              </span>
              <h1 className="text-5xl lg:text-6xl text-[#1f2937] mb-6 leading-tight">
                Risk and quality performance, visible while you can still change it
              </h1>
              <p className="text-xl text-[#4B5563] mb-8 leading-relaxed">
                For MA plans and health plan quality, risk, and finance leaders: improve Stars, protect shared savings, and reduce medical loss ratio, with clinical context kept alongside the financial signal rather than read in isolation.
              </p>
              <Button asChild size="lg" className="bg-[#34AFFB] hover:bg-[#34AFFB]/90 text-white">
                <Link to="/#contact">
                  Book a demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </Reveal>
            <Reveal as="div" direction="left" delay={150} className="hidden lg:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1721369967228-759150528943?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080"
                  alt="Health plan analytics and performance"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. Outcomes → modules */}
      <section className="py-20 bg-gradient-to-b from-white to-[#f9fbfc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <Lock className="h-8 w-8 text-[#34AFFB] mx-auto mb-4" />
            <h2 className="text-4xl text-[#1f2937] mb-4">Outcomes, powered by HealthBridge</h2>
            <p className="text-xl text-[#4B5563] max-w-3xl mx-auto">
              Governed and single-tenant by design, deployed in isolation per customer, so your data never co-mingles with another organization's data.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {outcomes.map((o, index) => {
              const Icon = o.icon;
              return (
                <Reveal as="div" key={index} delay={(index % 2) * 120} className="bg-white rounded-2xl p-8 border-l-4 border-[#34AFFB]/50 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-start gap-6">
                  <div className="h-14 w-14 bg-[#34AFFB]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="h-7 w-7 text-[#34AFFB]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1f2937] mb-2 text-xl leading-snug">{o.title}</h3>
                    <p className="text-[#34AFFB] text-sm uppercase tracking-wide font-semibold">{o.modules}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. Solutions: data & AI services beyond HealthBridge */}
      <section className="py-20 bg-[#F4F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl text-[#1f2937] mb-4">Applied Data & AI Solutions</h2>
            <p className="text-xl text-[#4B5563] max-w-3xl mx-auto">
              For the problems off-the-shelf products don't solve. We build custom data pipelines, models, and AI agents for health plans and the quality, risk, and finance teams carrying performance, engineered for your data and owned by you.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <Reveal as="div" key={index} delay={(index % 3) * 120} className="bg-white rounded-2xl p-12 shadow-sm">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`h-12 w-12 ${solution.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`h-6 w-6 ${solution.color}`} />
                    </div>
                    <h3 className="text-xl font-semibold text-[#1f2937]">{solution.title}</h3>
                  </div>
                  <p className="text-[#4B5563] leading-relaxed mb-6">{solution.description}</p>
                  <ul className="space-y-3">
                    {solution.bullets.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#4B5563] text-sm leading-relaxed">
                        <CheckCircle className="h-4 w-4 text-[#70D6C5] flex-shrink-0 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* From recent engagements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="text-4xl mb-4 text-[#1f2937]">What this looks like in practice</h2>
          </div>

          <div className="max-w-3xl mx-auto text-center">
            <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1F2937', marginBottom: '1rem' }}>
              Prior Authorization
            </span>
            <h3 className="text-xl font-semibold text-[#1f2937] mb-4 leading-snug">Prior Auth Status, Visible in Real Time</h3>
            <p className="text-[#4B5563] text-sm leading-relaxed">
              Members had no way to know where their prior authorization stood, so most requests turned into a call to the plan, and even then the answer wasn't always accurate. We built a live status-tracking solution that gives members direct, real-time visibility into their request, while giving the payer and providers a shared view to establish lead times, spot bottlenecks, and cut the time to decision.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#34AFFB' }}>48%</div>
                <div style={{ fontSize: '11px', color: '#4B5563' }}>Reduced Prior-auth status calls to the plan</div>
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#34AFFB' }}>35%</div>
                <div style={{ fontSize: '11px', color: '#4B5563' }}>Shorter average time to authorization</div>
              </div>
            </div>
          </div>
          <p style={{ fontSize: '12px', color: '#4B5563', opacity: 0.7, marginTop: '1.5rem', textAlign: 'center' }}>
            Annonymized; individual results vary.
          </p>
        </div>
      </section>

      {/* 4. Proof */}
      <section className="py-16 bg-[#F4F7F9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
            <h2 className="text-2xl text-[#1f2937] mb-4">Patients are the payoff.</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              When the data connects and the workflow flows, care becomes more proactive and whole.
            </p>
          </Reveal>
          <div className="ph-two-up">
            {[
              { stat: 'Up to 40%', label: 'Faster time-to-insight vs. legacy reporting' },
              { stat: 'Forecasted', label: 'Star cut points, priced before CMS publishes them' },
            ].map((item, index) => (
              <Reveal as="div" key={index} direction="scale" delay={index * 130} className="bg-[#f9fbfc] rounded-2xl p-8 text-center">
                <div className="text-3xl font-bold text-[#34AFFB] mb-3">{item.stat}</div>
                <p className="text-[#4B5563] text-sm">{item.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7. CTA */}
      <section className="relative overflow-hidden py-20 bg-gradient-to-r from-[#34AFFB] to-[#64748b]">
        <OrganicShapes variant="band" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl text-white mb-4">See risk and quality clearly — before the numbers lock.</h2>
          <Button asChild size="lg" className="bg-white text-[#34AFFB] hover:bg-white/90">
            <Link to="/#contact">
              Book a demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
